import {
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  GoogleAuthProvider,
  OAuthProvider,
  UserCredential,
  ActionCodeSettings,
  // MFA imports
  multiFactor,
  TotpMultiFactorGenerator,
  TotpSecret,
  getMultiFactorResolver,
  MultiFactorResolver,
  MultiFactorError,
  User,
} from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { app, auth, db, appId } from '@/config/firebase';
import type { UserProfile, AuthProvider } from '@/types';

// OAuth Providers
const googleProvider = new GoogleAuthProvider();
const microsoftProvider = new OAuthProvider('microsoft.com');
const appleProvider = new OAuthProvider('apple.com');

// Configure providers
googleProvider.setCustomParameters({
  prompt: 'select_account',
});

microsoftProvider.setCustomParameters({
  prompt: 'select_account',
});

appleProvider.addScope('email');
appleProvider.addScope('name');

/**
 * Sign in with Google OAuth
 */
export async function signInWithGoogle(): Promise<UserCredential> {
  return signInWithPopup(auth, googleProvider);
}

/**
 * Sign in with Microsoft OAuth
 */
export async function signInWithMicrosoft(): Promise<UserCredential> {
  return signInWithPopup(auth, microsoftProvider);
}

/**
 * Sign in with Apple OAuth
 */
export async function signInWithApple(): Promise<UserCredential> {
  return signInWithPopup(auth, appleProvider);
}

/**
 * Sign in with email and password
 */
export async function signInWithEmail(email: string, password: string): Promise<UserCredential> {
  return signInWithEmailAndPassword(auth, email, password);
}

/**
 * Create account with email and password
 */
export async function createAccountWithEmail(
  email: string,
  password: string
): Promise<UserCredential> {
  return createUserWithEmailAndPassword(auth, email, password);
}

/**
 * Email Link (Passwordless) Authentication
 */

// Storage keys for email link auth
const EMAIL_FOR_SIGN_IN_KEY = 'emailForSignIn';
const INVITE_CODE_FOR_SIGN_IN_KEY = 'inviteCodeForSignIn';

/**
 * Send a sign-in link to the user's email
 */
export async function sendEmailSignInLink(email: string, inviteCode?: string): Promise<void> {
  // Build the redirect URL - include invite code if provided
  const baseUrl = window.location.origin;
  let redirectUrl = `${baseUrl}/`;

  // We'll pass the invite code via the URL state if provided
  if (inviteCode) {
    redirectUrl += `?code=${encodeURIComponent(inviteCode)}`;
  }

  const actionCodeSettings: ActionCodeSettings = {
    url: redirectUrl,
    handleCodeInApp: true,
  };

  await sendSignInLinkToEmail(auth, email, actionCodeSettings);

  // Save the email and invite code locally for when the user returns
  window.localStorage.setItem(EMAIL_FOR_SIGN_IN_KEY, email);
  if (inviteCode) {
    window.localStorage.setItem(INVITE_CODE_FOR_SIGN_IN_KEY, inviteCode);
  }
}

/**
 * Check if the current URL is a sign-in email link
 */
export function isEmailSignInLink(url: string = window.location.href): boolean {
  return isSignInWithEmailLink(auth, url);
}

/**
 * Complete sign-in with email link
 * Returns the user credential if successful
 */
export async function completeEmailSignIn(
  email: string,
  url: string = window.location.href
): Promise<UserCredential> {
  const result = await signInWithEmailLink(auth, email, url);

  // Clear stored email after successful sign-in
  window.localStorage.removeItem(EMAIL_FOR_SIGN_IN_KEY);

  return result;
}

/**
 * Get the stored email for sign-in (used when user returns via email link)
 */
export function getStoredEmailForSignIn(): string | null {
  return window.localStorage.getItem(EMAIL_FOR_SIGN_IN_KEY);
}

/**
 * Get the stored invite code for sign-in (used when user returns via email link)
 */
export function getStoredInviteCodeForSignIn(): string | null {
  return window.localStorage.getItem(INVITE_CODE_FOR_SIGN_IN_KEY);
}

/**
 * Clear stored email link sign-in data
 */
export function clearEmailSignInData(): void {
  window.localStorage.removeItem(EMAIL_FOR_SIGN_IN_KEY);
  window.localStorage.removeItem(INVITE_CODE_FOR_SIGN_IN_KEY);
}

/**
 * Sign out the current user
 */
export async function signOut(): Promise<void> {
  return firebaseSignOut(auth);
}

/**
 * Call one of the invite code Cloud Functions.
 *
 * Invite codes are validated and consumed server-side so the invite_codes
 * collection can be locked down to leaders/admins (it used to be world-readable,
 * which let anyone list every valid code). Functions are lazy-loaded to keep the
 * initial bundle small.
 */
async function callInviteCodeFunction<T>(name: string, code: string): Promise<T> {
  const { getFunctions, httpsCallable } = await import('firebase/functions');
  const callable = httpsCallable<{ appId: string; code: string }, T>(getFunctions(app), name);
  const result = await callable({ appId, code });
  return result.data;
}

/**
 * Validate an invite code (works before sign-in).
 * Returns true if the code exists, is active, unexpired and has uses left.
 */
export async function validateInviteCode(code: string): Promise<boolean> {
  if (!code || code.trim() === '') {
    return false;
  }

  try {
    const result = await callInviteCodeFunction<{ valid: boolean }>(
      'validateInviteCode',
      code.trim().toUpperCase()
    );
    return result.valid === true;
  } catch (error) {
    console.error('Error validating invite code:', error);
    return false;
  }
}

/**
 * Consume an invite code (mark as used by the signed-in user).
 * The Cloud Function runs a transaction, so concurrent sign-ups cannot exceed
 * a limited-use code. Idempotent for the same user.
 */
export async function consumeInviteCode(code: string, _userId?: string): Promise<boolean> {
  if (!code || !code.trim()) {
    console.warn('Attempted to consume empty invite code');
    return false;
  }

  try {
    const result = await callInviteCodeFunction<{ success: boolean }>(
      'consumeInviteCode',
      code.trim().toUpperCase()
    );
    return result.success === true;
  } catch (error) {
    console.error('Error consuming invite code:', error);
    return false;
  }
}

/**
 * Get user profile from Firestore
 */
export async function getUserProfile(userId: string): Promise<UserProfile | null> {
  try {
    const profileRef = doc(db, 'artifacts', appId, 'users', userId, 'profile', 'info');
    const profileSnap = await getDoc(profileRef);

    if (profileSnap.exists()) {
      return profileSnap.data() as UserProfile;
    }

    return null;
  } catch (error) {
    console.error('Error fetching user profile:', error);
    return null;
  }
}

/**
 * Create or update user profile in Firestore
 */
export async function saveUserProfile(
  userId: string,
  profile: Omit<UserProfile, 'createdAt' | 'lastLoginAt'>,
  isNewUser: boolean = false
): Promise<void> {
  const profileRef = doc(db, 'artifacts', appId, 'users', userId, 'profile', 'info');

  // Create a placeholder document at the user level so the user appears in queries
  const userRef = doc(db, 'artifacts', appId, 'users', userId);

  if (isNewUser) {
    // Create placeholder document for user listing
    await setDoc(userRef, { exists: true }, { merge: true });

    await setDoc(profileRef, {
      ...profile,
      createdAt: serverTimestamp(),
      lastLoginAt: serverTimestamp(),
    });
  } else {
    await updateDoc(profileRef, {
      ...profile,
      lastLoginAt: serverTimestamp(),
    });
  }
}

/**
 * Update last login timestamp
 */
export async function updateLastLogin(userId: string): Promise<void> {
  try {
    const profileRef = doc(db, 'artifacts', appId, 'users', userId, 'profile', 'info');

    // Ensure user-level document exists for admin queries
    const userRef = doc(db, 'artifacts', appId, 'users', userId);
    await setDoc(userRef, { exists: true }, { merge: true });

    await updateDoc(profileRef, {
      lastLoginAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error updating last login:', error);
  }
}

/**
 * Determine auth provider from Firebase user
 */
export function getAuthProvider(providerId: string): AuthProvider {
  switch (providerId) {
    case 'google.com':
      return 'google';
    case 'microsoft.com':
      return 'microsoft';
    case 'apple.com':
      return 'apple';
    case 'emailLink':
      return 'emailLink';
    case 'password':
    default:
      return 'password';
  }
}

// ============================================================================
// Multi-Factor Authentication (MFA) - TOTP
// ============================================================================
// These functions are role-agnostic. UI visibility is controlled by
// canEnrollMfa() in utils/permissions.ts

/**
 * Result of starting TOTP enrollment
 */
export interface TotpEnrollmentData {
  secret: TotpSecret;
  totpUri: string;
  secretKey: string;
}

/**
 * Check if the current user has MFA enrolled
 */
export function isMfaEnrolled(user: User | null): boolean {
  if (!user) return false;
  const enrolledFactors = multiFactor(user).enrolledFactors;
  return enrolledFactors.length > 0;
}

/**
 * Get the enrolled MFA factors for a user
 */
export function getEnrolledMfaFactors(user: User | null) {
  if (!user) return [];
  return multiFactor(user).enrolledFactors;
}

/**
 * Start TOTP enrollment - generates a secret for QR code display
 * User must be recently authenticated (may need to re-authenticate first)
 */
export async function startTotpEnrollment(user: User): Promise<TotpEnrollmentData> {
  // Get multi-factor session
  const multiFactorSession = await multiFactor(user).getSession();

  // Generate TOTP secret
  const totpSecret = await TotpMultiFactorGenerator.generateSecret(multiFactorSession);

  // Get the key URI for QR code generation
  // Format: otpauth://totp/{issuer}:{account}?secret={secret}&issuer={issuer}
  const accountName = user.email || user.uid;
  const issuer = 'Spirit of Shabbat';
  const totpUri = totpSecret.generateQrCodeUrl(accountName, issuer);

  return {
    secret: totpSecret,
    totpUri,
    secretKey: totpSecret.secretKey,
  };
}

/**
 * Complete TOTP enrollment with verification code
 * Updates user profile with mfaEnabled flag
 */
export async function completeTotpEnrollment(
  user: User,
  totpSecret: TotpSecret,
  verificationCode: string,
  displayName: string = 'Authenticator App'
): Promise<void> {
  // Create assertion with the verification code
  const multiFactorAssertion = TotpMultiFactorGenerator.assertionForEnrollment(
    totpSecret,
    verificationCode
  );

  // Complete enrollment
  await multiFactor(user).enroll(multiFactorAssertion, displayName);

  // Update user profile with MFA status
  const profileRef = doc(db, 'artifacts', appId, 'users', user.uid, 'profile', 'info');
  await updateDoc(profileRef, {
    mfaEnabled: true,
    mfaEnrolledAt: serverTimestamp(),
  });
}

/**
 * Unenroll MFA for the current user
 * User must be recently authenticated
 */
export async function unenrollMfa(user: User): Promise<void> {
  const enrolledFactors = multiFactor(user).enrolledFactors;

  if (enrolledFactors.length === 0) {
    throw new Error('No MFA factors enrolled');
  }

  // Unenroll all factors (typically just one TOTP factor)
  for (const factor of enrolledFactors) {
    await multiFactor(user).unenroll(factor);
  }

  // Update user profile
  const profileRef = doc(db, 'artifacts', appId, 'users', user.uid, 'profile', 'info');
  await updateDoc(profileRef, {
    mfaEnabled: false,
    mfaEnrolledAt: null,
  });
}

/**
 * Check if an error is an MFA required error
 */
export function isMfaRequiredError(error: unknown): error is MultiFactorError {
  return (
    error !== null &&
    typeof error === 'object' &&
    'code' in error &&
    (error as { code: string }).code === 'auth/multi-factor-auth-required'
  );
}

/**
 * Get the MFA resolver from an MFA required error
 */
export function getMfaResolver(error: MultiFactorError): MultiFactorResolver {
  return getMultiFactorResolver(auth, error);
}

/**
 * Complete MFA sign-in with TOTP code
 */
export async function completeMfaSignIn(
  resolver: MultiFactorResolver,
  verificationCode: string,
  selectedIndex: number = 0
): Promise<UserCredential> {
  const hint = resolver.hints[selectedIndex];

  if (hint.factorId !== TotpMultiFactorGenerator.FACTOR_ID) {
    throw new Error('Only TOTP MFA is supported');
  }

  const multiFactorAssertion = TotpMultiFactorGenerator.assertionForSignIn(
    hint.uid,
    verificationCode
  );

  return resolver.resolveSignIn(multiFactorAssertion);
}

/**
 * Re-export auth instance for components that need direct access
 */
export { auth };
